import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';

export default function TdpUsernamePage() {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requestCount, setRequestCount] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  
  const submitTimeoutRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const countdownRef = useRef(null);

  const navigate = useNavigate();

  // Rate limiting: max 3 requests per minute
  const MAX_REQUESTS_PER_MINUTE = 3;

  const startCountdown = () => {
    setCountdown(60);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setRequestCount(0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmittingRef.current) return;
    
    // Check rate limiting
    if (requestCount >= MAX_REQUESTS_PER_MINUTE) {
      setShowRateLimitModal(true);
      return;
    }

    // Validate username
    const usernameRegex = /^[a-zA-Z0-9.]{5,15}$/;
    if (!usernameRegex.test(username.trim())) {
      setError('Username must be 5-15 characters long and contain only letters, numbers, and dots.');
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);
    setError('');
    setRequestCount(prev => prev + 1);

    try {
      const API_KEY = process.env.REACT_APP_API_KEY || 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O';
      const API_URL = process.env.REACT_APP_API_URL || 'https://eap.ethernet.edu.et/api';

      const response = await fetch(`${API_URL}/tdp-exam-result`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          username: username.trim().toLowerCase()
        })
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        // Success - navigate to result page with data
        navigate('/tdp-result', {
          state: {
            username: username.trim().toLowerCase(),
            resultData: data.data
          }
        });
      } else {
        // Handle different error types
        if (response.status === 404) {
          setError('No result found for this username. Please check the username and try again.');
        } else if (response.status === 403) {
          setError(data.message || 'Access restricted. Please contact your institution for assistance.');
        } else {
          setError(data.message || 'Failed to fetch result. Please try again.');
        }
      }
    } catch (err) {
      console.error('Network or fetch error:', err);
      setError('Network error occurred. Please check your connection and try again.');
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
      
      // Start countdown if this was the first request
      if (requestCount === 0) {
        startCountdown();
      }
    }
  };

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    if (error) setError('');
  };

  const closeRateLimitModal = () => {
    setShowRateLimitModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <i className="fa-solid fa-arrow-left mr-2" style={{ color: 'rgb(18,82,166)' }}></i>
            <span>Back</span>
          </button>
        </div>

        {/* Main Form Section */}
        <section className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-100">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="fa-solid fa-chart-line text-white text-2xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Enter your username
              </h2>
              <p className="text-gray-600 text-sm">
                Enter the username you used to take the exam to view your examination results
              </p>
            </div>

            {/* Username Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                    <i className="fa-regular fa-id-card" style={{ color: 'rgb(18,82,166)' }}></i>
                  </span>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={username}
                    onChange={handleUsernameChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-gray-300 transition-all duration-200"
                    style={{ 
                      '--tw-ring-color': 'rgb(18,82,166)',
                      '--tw-border-opacity': '1',
                      'border-color': 'rgb(18,82,166)'
                    }}
                    placeholder="Enter your username (5-15 characters)"
                    required
                    disabled={loading}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Username must be 5-15 characters long and contain only letters, numbers, and dots
                </p>
                <div className="mt-3 p-3 rounded-lg shadow-sm border-l-4" style={{ 
                  backgroundColor: 'rgba(18, 82, 166, 0.05)', 
                  borderLeftColor: 'rgb(18,82,166)',
                  border: '1px solid rgba(18, 82, 166, 0.1)'
                }}>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: 'rgb(18,82,166)' }}>
                      <i className="fa-solid fa-lightbulb text-white text-xs"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'rgb(18,82,166)' }}>
                        Username Format Tip
                      </p>
                      <p className="text-xs mt-1 text-gray-600">
                        If your username contains "/" (slash), please remove it. 
                        <span className="font-medium text-gray-700"> Example: "username/16" → "username"</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="w-full text-white py-3 rounded-lg transition-all duration-200 flex items-center justify-center font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'rgb(18,82,166)' }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fa-solid fa-spinner animate-spin mr-2"></i>
                    Loading Results...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-arrow-right-to-bracket mr-2"></i>
                    View Result
                  </>
                )}
              </button>

              {/* Error Display */}
              {error && (
                <div className="text-center">
                  <div className="inline-flex items-center justify-center p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    <i className="fa-solid fa-exclamation-triangle mr-2 text-red-500"></i>
                    <span className="text-sm font-medium">{error}</span>
                  </div>
                </div>
              )}
            </form>

            {/* Request Limit Indicator - Only show when limit is reached */}
            {requestCount >= MAX_REQUESTS_PER_MINUTE && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-red-700 font-medium">Request Limit Exceeded</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-red-600 font-medium">
                      Wait {countdown} seconds
                    </span>
                  </div>
                </div>
                <div className="w-full bg-red-200 rounded-full h-2 mt-2">
                  <div 
                    className="h-2 bg-red-500 rounded-full transition-all duration-300"
                    style={{ width: `${(countdown / 60) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Help Section */}
            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center justify-center">
                <span 
                  className="flex items-center cursor-pointer transition-colors duration-200 text-sm hover:opacity-80" 
                  style={{ color: 'rgb(18,82,166)' }}
                  onClick={() => window.open('https://t.me/moetdpsupport', '_blank')}
                >
                  <i className="fa-solid fa-headset mr-2"></i>
                  Contact Support
                </span>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Rate Limit Modal */}
      {showRateLimitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fa-solid fa-exclamation-triangle text-2xl text-yellow-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Rate Limit Exceeded</h3>
                <p className="text-gray-600 mb-6">
                  You've made too many requests. Please wait {countdown} seconds before trying again.
                </p>
                <button
                  onClick={closeRateLimitModal}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
